import { useState } from "react";
import {
  Button,
  Link,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Add20Regular,
  Delete20Regular,
  Edit24Regular,
  FolderAdd24Regular,
  FolderOpen20Regular,
  MoreVertical24Regular,
  Edit20Regular,
  TaskListLtr20Regular,
} from "@fluentui/react-icons";
import { Badge } from "@fluentui/react-components";
import { useData } from "../data/DataProvider";
import { useServizi, etichettaServizio } from "../data/useServizi";
import { cambiaStato, eAlbo, esitoLotti, oggettoGara, creazioneInterrotta, eliminaGara, eliminaLotto, loadLotti, lottiSenzaCartella, type Lotto } from "../data/gare";
import { impedimento } from "../data/cartelle";
import { avanzamento, componenti, loadChecklist, vociDi } from "../data/checklist";
import { useAsync } from "../lib/useAsync";
import { formatDate, formatDateTime, formatEuro } from "../lib/format";
import { goBack, navigate, replace } from "../lib/nav";
import { Shell } from "../components/Shell";
import { InfoRow, Section } from "../components/Section";
import { EsitoBadge, IscrizioneBadge, ScadenzaBadge, ValutazioneBadge } from "../components/Badges";
import { LottoDialog } from "../components/LottoDialog";
import { ErrorState, describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  badges: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  oggetto: { margin: 0, fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, lineHeight: tokens.lineHeightBase500 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: tokens.spacingVerticalM },
  lotto: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  lottoNum: { minWidth: "28px", fontWeight: tokens.fontWeightSemibold },
  lottoBody: { flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  link: { display: "inline-flex", alignItems: "center", gap: "4px", overflowWrap: "anywhere" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalM },
  cols: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(300px, 1fr)",
    gap: tokens.spacingHorizontalL,
    alignItems: "start",
    "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  col: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM, minWidth: 0 },
});

export function SchedaGara({ id }: { id: number }) {
  const s = useStyles();
  const data = useData();
  const gestore = data.roles.gestore;
  const { byId } = useServizi();
  const gara = useAsync(() => data.gare.get(id).then((x) => x ?? null), [data, id]);
  const lotti = useAsync(() => loadLotti(data, id), [data, id]);
  const checklist = useAsync(() => loadChecklist(data, id), [data, id]);
  const [dialog, setDialog] = useState<{ lotto?: Lotto } | undefined>();
  const [msg, setMsg] = useState<{ intent: "error" | "success"; text: string }>();

  const g = gara.data;
  if (gara.error || g === undefined || g === null)
    return (
      <Shell title="Gara" onBack={() => goBack()}>
        {gara.error ? (
          <ErrorState error={gara.error} />
        ) : g === null ? (
          <ErrorState error={new Error("Gara non trovata.")} />
        ) : (
          <Spinner style={{ marginTop: 48 }} />
        )}
      </Shell>
    );

  const f = g.fields;
  const albo = eAlbo(g);
  const servizio = byId.get(f.Servizio ?? 0);
  const elencoLotti = lotti.data ?? [];
  const blocco = impedimento(g, elencoLotti, servizio);
  const interrotta = creazioneInterrotta(g);
  const nuoviLotti = f.CartellaUrl ? lottiSenzaCartella(g, elencoLotti) : [];

  async function azione(fn: () => Promise<void>, ok?: string) {
    setMsg(undefined);
    try {
      await fn();
      if (ok) setMsg({ intent: "success", text: ok });
      await gara.reload();
      await lotti.reload();
    } catch (e) {
      setMsg({ intent: "error", text: describeError(e).detail });
    }
  }

  return (
    <Shell
      title={f.Codice ?? `Gara ${g.id}`}
      onBack={() => goBack()}
      actions={
        gestore && (
          <>
            <Button appearance="subtle" icon={<Edit24Regular />} onClick={() => navigate(`/g/${g.id}/modifica`)}>
              Modifica
            </Button>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="subtle" icon={<MoreVertical24Regular />} aria-label="Altre azioni" />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {f.Stato === "Archiviata" ? (
                    <MenuItem onClick={() => azione(() => cambiaStato(data, g, "Attiva"), "Gara riattivata.")}>Riattiva</MenuItem>
                  ) : (
                    <MenuItem onClick={() => azione(() => cambiaStato(data, g, "Archiviata"), "Gara archiviata.")}>Archivia</MenuItem>
                  )}
                  <MenuItem
                    icon={<Delete20Regular />}
                    onClick={() => {
                      if (window.confirm(`Eliminare ${albo ? "l'albo" : "la gara"} ${f.Codice}? L'operazione non si può annullare.`))
                        void eliminaGara(data, g, elencoLotti).then(
                          () => replace(albo ? "/albi" : "/gare/attive"),
                          (e: unknown) => setMsg({ intent: "error", text: describeError(e).detail }),
                        );
                    }}
                  >
                    Elimina
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </>
        )
      }
    >
      {msg && (
        <MessageBar intent={msg.intent}>
          <MessageBarBody>{msg.text}</MessageBarBody>
        </MessageBar>
      )}

      <div className={s.cols}>
        <div className={s.col}>
          <Section>
            <div className={s.badges}>
              {albo ? (
                <>
                  <Badge appearance="outline" color="brand" shape="rounded">
                    Albo
                  </Badge>
                  <IscrizioneBadge g={g} />
                </>
              ) : (
                <>
                  <ValutazioneBadge g={g} />
                  <ScadenzaBadge g={g} />
                  <EsitoBadge esito={f.PerLotti ? esitoLotti(elencoLotti) : f.Esito} />
                </>
              )}
            </div>
            <h1 className={s.oggetto}>{oggettoGara(g)}</h1>
            {albo ? (
              <div className={s.grid}>
                <InfoRow label="Ente">{f.Stazione ?? "—"}</InfoRow>
                <InfoRow label="Tipo di ente">{f.TipoEnte ?? "—"}</InfoRow>
                <InfoRow label="Servizio">{etichettaServizio(servizio)}</InfoRow>
                <InfoRow label="Data di iscrizione">{f.DataIscrizione ? formatDate(f.DataIscrizione) : "non ancora iscritta"}</InfoRow>
                <InfoRow label="Scadenza / rinnovo">{f.ScadenzaIscrizione ? formatDate(f.ScadenzaIscrizione) : "—"}</InfoRow>
              </div>
            ) : (
            <div className={s.grid}>
              <InfoRow label="Stazione appaltante">{f.Stazione ?? "—"}</InfoRow>
          <InfoRow label="Tipo di ente">{f.TipoEnte ?? "—"}</InfoRow>
              <InfoRow label="Servizio">{etichettaServizio(servizio)}</InfoRow>
              {!f.PerLotti && <InfoRow label="CIG">{f.CIG || "—"}</InfoRow>}
              <InfoRow label="Importo a base d'asta">{formatEuro(f.Importo)}</InfoRow>
              <InfoRow label="Scadenza presentazione">{formatDateTime(f.Scadenza)}</InfoRow>
              {f.ScadenzaInteresse && <InfoRow label="Manifestazione di interesse entro">{formatDateTime(f.ScadenzaInteresse)}</InfoRow>}
              {f.AperturaBuste && <InfoRow label="Apertura buste">{formatDateTime(f.AperturaBuste)}</InfoRow>}
          {!f.PerLotti && f.ImportoAggiudicato != null && <InfoRow label="Importo aggiudicato">{formatEuro(f.ImportoAggiudicato)}</InfoRow>}
            </div>
            )}
            {f.LinkBando && (
              <InfoRow label={albo ? "Avviso o portale" : "Bando"}>
                <Link href={f.LinkBando} target="_blank" rel="noreferrer">
                  {f.LinkBando}
                </Link>
              </InfoRow>
            )}
            {f.NoteValutazione && <InfoRow label={albo ? "Note" : "Note di valutazione"}>{f.NoteValutazione}</InfoRow>}
          </Section>

          {f.PerLotti && !albo && (
            <Section>
              <div className={s.head}>
                <h2 style={{ margin: 0, fontSize: tokens.fontSizeBase300 }}>Lotti</h2>
                {gestore && (
                  <Button size="small" icon={<Add20Regular />} onClick={() => setDialog({})}>
                    Aggiungi lotto
                  </Button>
                )}
              </div>
              {lotti.error ? (
                <ErrorState error={lotti.error} />
              ) : !lotti.data ? (
                <Spinner size="tiny" />
              ) : elencoLotti.length === 0 ? (
                <span className={s.meta}>Nessun lotto: aggiungili prima di creare le cartelle.</span>
              ) : (
                elencoLotti.map((l) => (
                  <div key={l.id} className={s.lotto}>
                    <span className={s.lottoNum}>L{l.fields.Numero}</span>
                    <div className={s.lottoBody}>
                      <span>
                    {l.fields.Title} {l.fields.Esito && <EsitoBadge esito={l.fields.Esito} />}
                  </span>
                      <span className={s.meta}>
                        {[
                      l.fields.CIG && `CIG ${l.fields.CIG}`,
                      l.fields.Importo != null && `base ${formatEuro(l.fields.Importo)}`,
                      l.fields.ImportoAggiudicato != null && `aggiudicato ${formatEuro(l.fields.ImportoAggiudicato)}`,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                      </span>
                      {l.fields.CartellaUrl && (
                        <Link className={s.link} href={l.fields.CartellaUrl} target="_blank" rel="noreferrer">
                          <FolderOpen20Regular /> {l.fields.NomeCartella}
                        </Link>
                      )}
                    </div>
                    {gestore && (
                      <>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Edit20Regular />}
                          aria-label={`Modifica lotto ${l.fields.Numero}`}
                          onClick={() => setDialog({ lotto: l })}
                        />
                        {!l.fields.CartellaId && (
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Delete20Regular />}
                            aria-label={`Elimina lotto ${l.fields.Numero}`}
                            onClick={() => window.confirm(`Eliminare il lotto ${l.fields.Numero}?`) && void azione(() => eliminaLotto(data, l))}
                          />
                        )}
                      </>
                    )}
                  </div>
                ))
              )}
            </Section>
          )}
        </div>
        <div className={s.col}>
          <Section title="Cartelle">
            {f.CartellaUrl ? (
              <>
                <Link className={s.link} href={f.CartellaUrl} target="_blank" rel="noreferrer">
                  <FolderOpen20Regular /> {f.PerLotti ? "Apri la cartella che contiene i lotti" : f.NomeCartella}
                </Link>
                {f.Posizione && <span className={s.meta}>Posizione: {f.Posizione}</span>}
                {gestore && nuoviLotti.length > 0 && (
                  <div>
                    <Button icon={<FolderAdd24Regular />} onClick={() => navigate(`/g/${g.id}/cartelle`)}>
                      {nuoviLotti.length === 1
                        ? `Crea la cartella del lotto ${nuoviLotti[0]!.fields.Numero}`
                        : `Crea le cartelle dei ${nuoviLotti.length} lotti nuovi`}
                    </Button>
                  </div>
                )}
              </>
            ) : !gestore ? (
              <span className={s.meta}>Le cartelle non sono ancora state create.</span>
            ) : blocco && !interrotta ? (
              <span className={s.meta}>{blocco}</span>
            ) : (
              <>
                {interrotta && (
                  <MessageBar intent="warning">
                    <MessageBarBody>La creazione delle cartelle si è interrotta: completala ({f.Posizione}).</MessageBarBody>
                  </MessageBar>
                )}
                <div>
                  <Button appearance="primary" icon={<FolderAdd24Regular />} onClick={() => navigate(`/g/${g.id}/cartelle`)} disabled={!!blocco}>
                    {interrotta ? "Completa la creazione" : albo ? "Crea la cartella dell'albo" : f.PerLotti ? `Crea le cartelle dei ${elencoLotti.length} lotti` : "Crea la cartella di gara"}
                  </Button>
                </div>
                {blocco && <span className={s.meta}>{blocco}</span>}
              </>
            )}
          </Section>

          <Section title="Checklist documenti">
            {checklist.error ? (
              <span className={s.meta}>Checklist non disponibile: {describeError(checklist.error).detail}</span>
            ) : !checklist.data || !lotti.data ? (
              <Spinner size="tiny" />
            ) : (
              <>
                {componenti(g).length > 1 && <span className={s.meta}>RTI: {componenti(g).join(", ")}</span>}
                {checklist.data.length === 0 ? (
                  <span className={s.meta}>Non ancora preparata.</span>
                ) : f.PerLotti ? (
                  elencoLotti.map((l) => {
                    const a = avanzamento(vociDi(checklist.data!, l));
                    return (
                      <span key={l.id}>
                        Lotto {l.fields.Numero}: {a.totale ? `${a.pronti} di ${a.totale} pronti` : <span className={s.meta}>non preparata</span>}
                      </span>
                    );
                  })
                ) : (
                  (() => {
                    const a = avanzamento(vociDi(checklist.data, undefined));
                    return (
                      <span>
                        {a.pronti} di {a.totale} documenti pronti
                      </span>
                    );
                  })()
                )}
                <div>
                  <Button icon={<TaskListLtr20Regular />} onClick={() => navigate(`/g/${g.id}/checklist`)}>
                    {checklist.data.length === 0 && (data.roles.gestore || data.roles.collaboratore) ? "Prepara la checklist" : "Apri la checklist"}
                  </Button>
                </div>
              </>
            )}
          </Section>

          <Section title="Storico">
            <InfoRow label="Inserita">
              {formatDateTime(g.createdAt)}
              {g.createdBy?.displayName ? ` · ${g.createdBy.displayName}` : ""}
            </InfoRow>
            <InfoRow label="Ultima modifica">
              {formatDateTime(g.modifiedAt)}
              {g.modifiedBy?.displayName ? ` · ${g.modifiedBy.displayName}` : ""}
            </InfoRow>
          </Section>
        </div>
      </div>

      {dialog && (
        <LottoDialog
          gara={g}
          lotto={dialog.lotto}
          altri={elencoLotti}
          onClose={(saved) => {
            setDialog(undefined);
            if (saved) void lotti.reload();
          }}
        />
      )}
    </Shell>
  );
}
